import { Box, Flex, Link } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { NAVBAR_HEIGHT } from 'constants/editor';
import { MY_DESIGN_REDIRECT_LINK, TEMPLATES_RECOMMENDED_REDIRECT_LINK } from 'constants/links';
import { UPGRADE } from 'constants/mixpanel';
import { useChameleon } from 'libs/third-party/Chameleon';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { selectShowCollaborationWarning } from 'modules/Editor/store/editorSettingsSelector';
import { setDownloadModal } from 'modules/Editor/store/editorSettingsSlice';
import { DownloadModalTrigger } from 'modules/Editor/store/editorSettingsSlice.types';
import { useAppDispatch, useAppSelector } from 'modules/Editor/store/hooks';
import { selectInfographTitle } from 'modules/Editor/store/infographSelector';
import { setTitle } from 'modules/Editor/store/infographSlice';
import { UserTypes, useUser } from 'modules/User';
import { ToolbarDivider } from 'modules/common/components/Toolbar/ToolbarDivider';
import { ReactElement, memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isStandalone } from 'utils/environment';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';

import { AutosaveIndicator } from './AutosaveIndicator';
import { CollaborationAlert } from './CollaborationAlertModal';
import { DownloadButton } from './DownloadButton';
import { DownloadMenu } from './DownloadMenu';
import { FileMenu } from './FileMenu';
import { HelpLink } from './Helplink';
import { InviteTeamButton } from './InviteTeamButton';
import { ShareButton } from './ShareButton';
import { ShareMenu } from './ShareMenu';
import { SignUpButton } from './SignUpButton';
import { TitleInput } from './TitleInput';
import { UndoMenu } from './UndoMenu';
import { UpgradeButton } from './UpgradeButton';
import { UserAvatar } from './UserAvatar';

const NavWrapper = styled.nav`
  color: var(--vg-colors-gray-600);
  width: 100%;
  height: ${NAVBAR_HEIGHT}px;
  display: flex;
  align-items: center;
  padding: 0 20px;
  box-shadow: 0 0 6px rgb(0 0 0 / 18%);
  position: relative;
`;

export const Navbar = memo((): ReactElement => {
  const { data: userData, isFreeUser, isBusinessUser } = useUser();
  const dispatch = useAppDispatch();
  const { trackChameleon } = useChameleon();

  const { t } = useTranslation('common_navbar', { useSuspense: false });

  const infographTitle = useAppSelector(selectInfographTitle);
  const showCollaborationWarning = useAppSelector(selectShowCollaborationWarning);

  const updateInfographTitle = useCallback(
    (newTitle: string) => {
      if (!newTitle) {
        return;
      }

      dispatch(setTitle({ title: newTitle }));
    },
    [dispatch],
  );

  const handleInviteTeamClick = useCallback(() => {
    if (isBusinessUser) {
      Mixpanel.track(UPGRADE.eventName, {
        feature: UPGRADE.feature.InviteTeamMember,
        plan: UserTypes.Business,
      });
      trackChameleon(UPGRADE.eventName);

      setAnalyticsUpgradeFeatureProp(UPGRADE.feature.InviteTeamMember);
      dispatch(setDownloadModal({ isOpen: true, trigger: DownloadModalTrigger.ShareButton }));
    }
  }, [isBusinessUser, dispatch, trackChameleon]);

  // Flag to check which button is highlighted in the navbar - share or download
  const showShareOrDownloadButton = useMemo(() => isFreeUser, [isFreeUser]);

  return (
    <NavWrapper data-testid='navbar'>
      <Box mr={2}>
        <Flex align='center' gap='2'>
          <Link href={MY_DESIGN_REDIRECT_LINK} variant='nav-link'>
            {t('home')}
          </Link>
          <Link href={TEMPLATES_RECOMMENDED_REDIRECT_LINK} variant='nav-link'>
            {t('templates')}
          </Link>
        </Flex>
      </Box>
      <ToolbarDivider mr={2} ml={2} />
      <FileMenu />
      <Box ml={2}>
        <TitleInput title={infographTitle} onSubmit={updateInfographTitle} />
      </Box>
      <UndoMenu />
      <Box ml='auto' minW='40px'>
        <AutosaveIndicator />
      </Box>
      <ToolbarDivider mr={2} ml={2} />
      <Box mr={2}>{showShareOrDownloadButton ? <ShareMenu /> : <DownloadMenu />}</Box>
      <Flex mr={2} gap={2}>
        {showShareOrDownloadButton ? <DownloadButton /> : <ShareButton />}
        <InviteTeamButton userType={userData?.user_type} onClick={handleInviteTeamClick} />
        <UpgradeButton userType={userData?.user_type} />
        {userData === undefined && !isStandalone && <SignUpButton />}
      </Flex>
      <Box mx={2} zIndex='navbar'>
        <HelpLink />
      </Box>
      <UserAvatar />
      {showCollaborationWarning && <CollaborationAlert isOpen={showCollaborationWarning} />}
    </NavWrapper>
  );
});
Navbar.displayName = 'Navbar';
